import { Link } from 'gatsby-plugin-react-i18next'
import Subtitle from 'src/components/ui/Subtitle'
import H3 from 'src/components/ui/title/H3'
import React, { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { gsap } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import getPlaceholderImages from 'src/helpers/caseStudyImages'

import styled from 'styled-components'

const ImageContainer = styled.div<{ $isFullWidth: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-container-medium, 24px);
  background-color: var(--color-surface, #f5f5f5);
  /* On mobile, use a vertical aspect ratio (4:5) */
  aspect-ratio: 4 / 5;
  
  @media (min-width: 768px) {
    aspect-ratio: auto;
    height: ${({ $isFullWidth }) => ($isFullWidth ? '480px' : '350px')};
  }
  @media (min-width: 1280px) {
    height: ${({ $isFullWidth }) => ($isFullWidth ? '680px' : '520px')};
  }
  @media (min-width: 1536px) {
    height: ${({ $isFullWidth }) => ($isFullWidth ? '800px' : '640px')};
  }
`

const RevealImageWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  clip-path: inset(100% 0 0 0);
  transition: clip-path 750ms cubic-bezier(0.16, 1, 0.3, 1);
  will-change: clip-path;

  @media (hover: hover) and (pointer: fine) {
    .group:hover & {
      clip-path: inset(0 0 0 0);
    }
  }
`

const RevealImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.14) translateY(-6%);
  transition: transform 750ms cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;

  @media (hover: hover) and (pointer: fine) {
    .group:hover & {
      transform: scale(1) translateY(0);
    }
  }
`

const BaseImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1) translateY(0);
  transition: transform 750ms cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;

  @media (hover: hover) and (pointer: fine) {
    .group:hover & {
      transform: scale(1.06) translateY(3%);
    }
  }
`

interface ICaseStudiesGridItemProps {
  title: string
  subtitle: string
  firstPartOfTitle?: string
  secondPartOfTitle?: string
  tags: string[] | string
  ctaLink: string
  image: {
    url: string
    alt: string
  }
  detailImage?: {
    url: string
    alt?: string
  }
  className?: string
  isFullWidth?: boolean
}

const CaseStudiesGridItem: FC<ICaseStudiesGridItemProps> = ({
  title,
  subtitle,
  firstPartOfTitle,
  secondPartOfTitle,
  tags,
  ctaLink,
  image,
  detailImage,
  className = '',
  isFullWidth = false,
}) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const timeline = useRef<any>(null)

  const [isMounted, setIsMounted] = useState(false)

  const resolvedDetailImageUrl = (() => {
    if (detailImage?.url) {
      return detailImage.url !== image.url ? detailImage.url : null
    }

    const cleanSlug = (ctaLink || '')
      .replace(/^\/case-studies\/?/, '')
      .replace(/^\//, '')

    if (cleanSlug) {
      if (cleanSlug.includes('rosa')) {
        return '/images/placeholders/rosa/rosa-2.webp'
      }
      if (!cleanSlug.startsWith('http')) {
        const normalizedSlug = cleanSlug === 'fuelo' ? 'fg-energy' : cleanSlug
        const placeholderImages = getPlaceholderImages(normalizedSlug)
        if (placeholderImages && placeholderImages.length > 0 && placeholderImages[0] !== image.url) {
          return placeholderImages[0]
        }
      }
    }
    return null
  })()

  useEffect(() => {
    setIsMounted(true)
    if (!timeline.current) {
      timeline.current = gsap.timeline()
    }
    if (itemRef.current) {
      createShowFromBottomAnimation(timeline.current, itemRef.current)
    }

    if (cursorRef.current) {
      gsap.set(cursorRef.current, { xPercent: -50, yPercent: -50 })
    }
  }, [])

  const handleMouseMove = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.15,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      // Instantly position the cursor mapped to viewport to prevent teleporting
      gsap.set(cursorRef.current, { x: e.clientX, y: e.clientY })

      gsap.to(cursorRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
      })
    }
  }

  const handleMouseLeave = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      })
    }
  }

  return (
    <div
      ref={itemRef}
      className={`opacity-0 ${className} group relative`}
    >
      {/* Custom Cursor Circle using Portal to stay pinned to screen on scroll */}
      {isMounted && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={cursorRef}
            className="pointer-events-none fixed left-constant-none top-constant-none z-50 flex h-colossal w-colossal items-center justify-center rounded-constant-full bg-primary text-on-surface-inverse dark:text-on-primary-unstable opacity-0"
            style={{ transform: 'scale(0)' }}
          >
            <span className="font-palanquin text-title-small font-bold uppercase tracking-widest">
              View
            </span>
          </div>,
          document.body
        )
        : null}

      <Link to={ctaLink} className="block w-full">
        {/* Image Container */}
        <div
          className="relative group overflow-hidden md:cursor-none"
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ImageContainer $isFullWidth={isFullWidth}>
            {/* Primary Cover Image */}
            <BaseImage
              src={image.url}
              alt={image.alt}
            />

            {/* Awwwards Curtain Reveal Image */}
            {resolvedDetailImageUrl && (
              <RevealImageWrapper>
                <RevealImage
                  src={resolvedDetailImageUrl}
                  alt={detailImage?.alt || image.alt}
                />
              </RevealImageWrapper>
            )}

            {/* Dark gradient overlay for bottom text contrast */}
            <div className="absolute inset-x-constant-none bottom-constant-none h-3/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-10" />

            {/* Categories / Tags as colored pills top-right */}
            {tags && (
              <div className="absolute top-semi-large right-semi-large md:top-large md:right-large lg:top-semi-large lg:right-semi-large xl:top-large xl:right-large flex flex-wrap gap-extra-small justify-end z-20 transition-transform duration-500 ease-out transform md:group-hover:-translate-x-2">
                {(Array.isArray(tags) ? tags.flatMap((t) => (typeof t === 'string' ? t.split(',') : t)) : typeof tags === 'string' ? tags.split(',') : [tags])
                  .map((tag) => (typeof tag === 'string' ? tag.trim() : tag))
                  .filter(Boolean)
                  .map((tag, index) => (
                    <span
                      key={index}
                      className="rounded-constant-full px-medium py-extra-small md:px-large md:py-base lg:px-medium lg:py-extra-small xl:px-large xl:py-base text-label-large md:text-body-medium lg:text-label-large xl:text-body-medium font-bold uppercase tracking-wider text-on-surface-inverse dark:text-on-surface-dark backdrop-blur-higher bg-elevation-higher"
                    >
                      {tag}
                    </span>
                  ))}
              </div>
            )}

            {/* Subtitle & Title at bottom */}
            <div className="absolute bottom-constant-none left-constant-none right-constant-none p-semi-large md:p-large lg:p-semi-large xl:p-extra-large z-20 flex flex-col items-start gap-extra-small md:gap-small transition-transform duration-500 ease-out transform md:group-hover:-translate-y-2">
              {(title || subtitle) && (
                <Subtitle className="!mb-0 backdrop-blur-higher dark:bg-elevation-lower !text-on-surface-inverse dark:!text-on-surface-dark">
                  {title || subtitle}
                </Subtitle>
              )}
              <H3
                firstPart={firstPartOfTitle || title}
                secondPart={secondPartOfTitle || ''}
                variant="inverse"
                className="lg:text-headline-small xl:text-headline-large dark:[&_span]:!text-on-surface-dark"
              />
            </div>
          </ImageContainer>
        </div>
      </Link>
    </div>
  )
}

export default CaseStudiesGridItem
